import { useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

interface DivisionRoutingConfig {
  onDivisionChange?: (division: 'software' | 'hardware' | 'services') => void
  onProductRoute?: (product: string) => void
}

// Map of product routes to their divisions
const productToDivision: Record<string, 'software' | 'hardware' | 'services'> = {
  codered: 'software',
  beyondreal: 'software',
  macx: 'software',
  'macx-handheld': 'hardware',
  'melanin-devices': 'hardware',
}

export const useDivisionRouting = (config: DivisionRoutingConfig = {}) => {
  const location = useLocation()
  const navigate = useNavigate()
  const isInitialMount = useRef(true)
  const lastPath = useRef(location.pathname)
  const scrollPositions = useRef<Record<string, number>>({})

  // Save scroll position before navigation
  const saveScrollPosition = (path: string) => {
    scrollPositions.current[path] = window.scrollY
  }

  // Restore scroll position after navigation
  const restoreScrollPosition = (path: string) => {
    const savedPosition = scrollPositions.current[path]
    if (savedPosition !== undefined) {
      setTimeout(() => {
        window.scrollTo({ top: savedPosition, behavior: 'auto' })
      }, 100)
    }
  }

  // Handle route changes - only update state, NO auto-scrolling
  useEffect(() => {
    const path = location.pathname.substring(1) // Remove leading slash

    // Skip on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false
      
      // Only handle routing if we're on a division/product route (not home page)
      if (path && path !== '') {
        handleRouteChange(path)
      }
      return
    }

    // Only handle if path actually changed
    if (lastPath.current !== location.pathname) {
      // Save scroll position of previous path
      saveScrollPosition(lastPath.current)
      lastPath.current = location.pathname
      handleRouteChange(path)
    }
  }, [location.pathname])

  // Handle browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      const path = window.location.pathname.substring(1)
      // Check if we should restore scroll position (for back/forward navigation)
      if (path in scrollPositions.current) {
        restoreScrollPosition(path)
      } else {
        handleRouteChange(path)
      }
    }

    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [])

  const handleRouteChange = (path: string) => {
    // Handle division routes - only trigger callbacks, NO auto-scrolling
    if (path === 'software' || path === 'hardware' || path === 'services') {
      config.onDivisionChange?.(path)
      return
    }

    // Handle product routes - only trigger callbacks, NO auto-scrolling
    if (path in productToDivision) {
      const division = productToDivision[path]
      config.onDivisionChange?.(division)
      config.onProductRoute?.(path)
      return
    }
  }

  // Navigate to a division and update URL - NO auto-scroll, user scrolls manually
  const navigateToDivision = (division: 'software' | 'hardware' | 'services') => {
    navigate(`/${division}`, { replace: false })
  }

  // Navigate to a product and update URL - NO auto-scroll, user scrolls manually
  const navigateToProduct = (product: string) => {
    navigate(`/${product}`, { replace: false })
  }

  // Update URL without navigation (for tab changes)
  const updateUrl = (path: string, replace: boolean = true) => {
    if (replace) {
      window.history.replaceState(null, '', `/${path}`)
    } else {
      window.history.pushState(null, '', `/${path}`)
    }
  }

  return {
    navigateToDivision,
    navigateToProduct,
    updateUrl,
    currentPath: location.pathname,
  }
}
